import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { UpdateMultipleElementsCommand } from './UpdateMultipleElementsCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export type TimelineUpdates = {
  width: number;
  height: number;
};

/**
 * Command that updates timeline dimensions and multiple elements atomically.
 * Combines timeline dimension changes (from ChangeAspectRatioCommand) with
 * multiple element updates (from UpdateMultipleElementsCommand).
 *
 * Used primarily for aspect ratio changes in quote cards where we need to:
 * 1. Update timeline width/height
 * 2. Update multiple elements to fit new dimensions
 * 3. Create a single undo point for the entire operation
 */
export class UpdateTimelineAndElementsCommand implements Command {
  private editor: VideoEditor;
  private newWidth: number;
  private newHeight: number;
  private updateElementsCommand: UpdateMultipleElementsCommand;
  private readonly _shouldCreateUndoRedoEntry: boolean;

  // State for undo/redo
  private oldWidth: number = 0;
  private oldHeight: number = 0;
  private executed: boolean = false;

  constructor(
    editor: VideoEditor,
    timelineUpdates: TimelineUpdates,
    elementUpdates: Map<string, Partial<Element>>,
    createUndoEntry: boolean = true,
  ) {
    this.editor = editor;
    this.newWidth = timelineUpdates.width;
    this.newHeight = timelineUpdates.height;
    this._shouldCreateUndoRedoEntry = createUndoEntry;

    // Create the sub-command but don't execute it through executeCommand()
    // to prevent it from creating its own undo entry
    this.updateElementsCommand = new UpdateMultipleElementsCommand(
      editor,
      elementUpdates,
    );

    this.storeOldState();
  }

  getName(): string {
    return 'UpdateTimelineAndElementsCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    // 1. Update timeline dimensions
    if (!this.setTimelineDimensions(this.newWidth, this.newHeight)) {
      return false;
    }

    // 2. Update elements by calling sub-command execute() directly
    // This prevents the sub-command from creating its own undo entry
    const success = this.updateElementsCommand.execute();

    if (success) {
      this.executed = true;
      // Note: updateElementsCommand already calls markDirty and notifyObservers
      // so we don't need to duplicate those calls here
    }

    return success;
  }

  undo(): boolean {
    if (!this.executed) return false;

    // 1. Undo elements first by calling sub-command undo() directly
    const elementsUndone = this.updateElementsCommand.undo();
    if (!elementsUndone) return false;

    // 2. Restore timeline dimensions
    if (!this.setTimelineDimensions(this.oldWidth, this.oldHeight)) {
      return false;
    }

    this.executed = false;
    // Note: updateElementsCommand.undo() already calls markDirty and notifyObservers
    // so we don't need to duplicate those calls here

    return true;
  }

  redo(): boolean {
    if (this.executed) return false;
    return this.execute();
  }

  getDescription(): string {
    return `Update timeline (${this.newWidth}x${
      this.newHeight
    }) and ${this.updateElementsCommand.getDescription()}`;
  }

  private storeOldState(): void {
    // Store old timeline dimensions
    this.oldWidth = this.editor.timeline.width;
    this.oldHeight = this.editor.timeline.height;

    // Element snapshots are stored by the sub-command
  }

  private setTimelineDimensions(width: number, height: number): boolean {
    try {
      this.editor.timeline.setWidth(width);
      this.editor.timeline.setHeight(height);
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to set timeline dimensions',
        width,
        height,
        details: error,
      });
      return false;
    }
  }
}
